import { useState, useRef, useEffect } from 'react'
import { Filter, X, Search as SearchIcon } from 'lucide-react'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { SingleLanguageSelector } from './SingleLanguageSelector'
import { MultiLanguageSelector } from './MultiLanguageSelector'
import { BOOK_GENRES } from '../../types'
import { ALL_LANGUAGES } from '../../lib/constants/languages'
import { cn } from '../../lib/utils'
import { useTranslation } from 'react-i18next'

export type SortOption = 'popularity' | 'title-asc' | 'title-desc' | 'author-asc' | 'author-desc' | 'newest'

export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const
export type CefrLevel = typeof CEFR_LEVELS[number]

interface SearchProps {
  // Search
  searchTerm: string
  onSearchChange: (term: string) => void
  onSearchSubmit: (e: React.FormEvent) => void
  searchPlaceholder?: string
  
  // Filters
  selectedLanguage: string | string[]
  selectedGenre: string
  selectedOriginalLanguage: string
  selectedSort: SortOption | ''
  selectedGrades?: string[]
  onLanguageChange: (language: string | string[]) => void
  onGenreChange: (genre: string) => void
  onOriginalLanguageChange: (language: string) => void
  onSortChange: (sort: SortOption | '') => void
  onGradeChange?: (grades: string[]) => void
  onApplyFilters: () => void
  onClearFilters: () => void
  
  // Filter visibility
  showGenreFilter?: boolean
  showOriginalLanguageFilter?: boolean
  showSortFilter?: boolean
  showGradeFilter?: boolean
  useMultiSelect?: boolean
  
  // Data
  topLanguageCodes?: string[]
  translatedToLanguages?: Array<{ code: string; name: string; flag: string }>
  originalLanguages?: Array<{ code: string; name: string; flag: string }>
}

export function Search({
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder,
  selectedLanguage,
  selectedGenre,
  selectedOriginalLanguage,
  selectedSort,
  selectedGrades = [],
  onLanguageChange,
  onGenreChange,
  onOriginalLanguageChange,
  onSortChange,
  onGradeChange,
  onApplyFilters,
  onClearFilters,
  showGenreFilter = false,
  showOriginalLanguageFilter = false,
  showSortFilter = false,
  showGradeFilter = false,
  useMultiSelect = false,
  topLanguageCodes,
  translatedToLanguages,
  originalLanguages
}: SearchProps) {
  const { t } = useTranslation('common')
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'popularity', label: t('filters.popularity') },
    { value: 'newest', label: t('filters.newest') },
    { value: 'title-asc', label: t('filters.titleAsc') },
    { value: 'title-desc', label: t('filters.titleDesc') },
    { value: 'author-asc', label: t('filters.authorAsc') },
    { value: 'author-desc', label: t('filters.authorDesc') },
  ]

  // Check if any filters are active
  const isLanguageActive = useMultiSelect 
    ? Array.isArray(selectedLanguage) && selectedLanguage.length > 0
    : typeof selectedLanguage === 'string' && selectedLanguage !== ''
  const isGradeActive = selectedGrades.length > 0
  const hasActiveFilters = isLanguageActive || selectedGenre !== '' || selectedOriginalLanguage !== '' || selectedSort !== '' || isGradeActive

  // Count active filters
  const activeFilterCount = (isLanguageActive ? 1 : 0) + (selectedGenre ? 1 : 0) + (selectedOriginalLanguage ? 1 : 0) + (selectedSort ? 1 : 0) + (isGradeActive ? 1 : 0)

  // For single-select mode
  const languageCode = useMultiSelect 
    ? '' 
    : (typeof selectedLanguage === 'string' ? selectedLanguage : '')
  
  // For multi-select
  const selectedLanguageCodes = useMultiSelect && Array.isArray(selectedLanguage)
    ? selectedLanguage
    : []

  const originalLanguageCode = selectedOriginalLanguage || ''

  // Handle language change from SingleLanguageSelector
  const handleSingleLanguageChange = (code: string) => {
    onLanguageChange(code)
  }

  // Handle language change from MultiLanguageSelector
  const handleMultiLanguageChange = (codes: string[]) => {
    onLanguageChange(codes)
  }

  const handleOriginalLanguageChange = (code: string) => {
    onOriginalLanguageChange(code)
  }

  const handleApplyFilters = () => {
    onApplyFilters()
    setIsFilterPanelOpen(false)
  }

  const handleClearFilters = () => {
    onClearFilters()
  }

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFilterPanelOpen) {
        setIsFilterPanelOpen(false)
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isFilterPanelOpen])

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isFilterPanelOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    
    return () => {
      document.body.style.overflow = ''
    }
  }, [isFilterPanelOpen])

  return (
    <>
      {/* Search Bar */}
      <div className="search-component mb-6">
        <form onSubmit={onSearchSubmit} className="flex gap-2">
          {/* Filter Button */}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setIsFilterPanelOpen(true)}
            className="filter-toggle-btn relative h-12 w-12 shrink-0 border-2"
            aria-label={t('filters.openFilters')}
          >
            <Filter className="h-5 w-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>

          {/* Search Input with Lens Button */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder || t('library.searchPlaceholderLibrary')}
              className="search-input w-full h-12 pl-4 pr-14 border-2 border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            {/* Lens Icon Button */}
            <button
              type="submit"
              className="search-lens-btn absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-md bg-muted hover:bg-muted/80 transition-colors"
              aria-label={t('library.search')}
            >
              <SearchIcon className="h-5 w-5 text-foreground" />
            </button>
          </div>
        </form>
      </div>

      {/* Filter Panel Overlay */}
      {isFilterPanelOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={() => setIsFilterPanelOpen(false)}
            aria-hidden="true"
          />
          
          {/* Filter Panel */}
          <div
            ref={panelRef}
            className={cn(
              "filter-panel fixed top-0 left-0 h-full w-full sm:w-96 bg-muted/95 backdrop-blur-sm z-50 shadow-2xl",
              "transform transition-transform duration-300 ease-in-out",
              "flex flex-col"
            )}
            style={{
              backgroundColor: 'color-mix(in srgb, hsl(var(--muted)) 95%, black 5%)'
            }}
          >
            {/* Panel Header */}
            <div className="filter-panel-header sticky top-0 z-10 bg-background/90 backdrop-blur-sm border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Filter className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">
                    {t('filters.title')}
                  </h2>
                  {activeFilterCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFilterPanelOpen(false)}
                  className="h-8 w-8"
                  aria-label={t('filters.closeFilters')}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Panel Content - Scrollable */}
            <div className="filter-panel-content flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-6">
                {/* Translated Language Filter */}
                <div className="filter-group space-y-3">
                  <Label className="text-sm font-semibold text-foreground">
                    {t('filters.showBooksTranslatedTo')}
                  </Label>
                  {useMultiSelect ? (
                    <MultiLanguageSelector
                      value={selectedLanguageCodes}
                      onChange={handleMultiLanguageChange}
                      showActiveFilters={false}
                      onClear={() => onLanguageChange([])}
                      topLanguageCodes={topLanguageCodes}
                      applyMode={false}
                    />
                  ) : (
                    <SingleLanguageSelector
                      value={languageCode}
                      onChange={handleSingleLanguageChange}
                      topLanguageCodes={topLanguageCodes}
                      languages={translatedToLanguages !== undefined ? translatedToLanguages : ALL_LANGUAGES}
                      emptyMessage={t('filters.noTranslationsFound')}
                    />
                  )}
                </div>

                {/* Genre Filter */}
                {showGenreFilter && (
                  <div className="filter-group space-y-3">
                    <Label htmlFor="genre-filter" className="text-sm font-semibold text-foreground">
                      {t('filters.genre')}
                    </Label>
                    <select
                      id="genre-filter"
                      value={selectedGenre}
                      onChange={(e) => onGenreChange(e.target.value)}
                      className="genre-select w-full px-4 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">{t('filters.allGenres')}</option>
                      {BOOK_GENRES.map((genre) => (
                        <option key={genre} value={genre}>
                          {genre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* CEFR Grade Filter */}
                {showGradeFilter && onGradeChange && (
                  <div className="filter-group space-y-3">
                    <Label className="text-sm font-semibold text-foreground">
                      {t('filters.grade')}
                    </Label>
                    <div className="grade-checkboxes grid grid-cols-3 gap-2">
                      {CEFR_LEVELS.map((level) => (
                        <label
                          key={level}
                          className={cn(
                            "grade-checkbox-item flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors",
                            selectedGrades.includes(level)
                              ? "bg-primary/10 border-primary text-primary"
                              : "bg-background border-border hover:border-primary/50"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selectedGrades.includes(level)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onGradeChange([...selectedGrades, level])
                              } else {
                                onGradeChange(selectedGrades.filter(g => g !== level))
                              }
                            }}
                            className="sr-only"
                          />
                          <span className={cn(
                            "w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                            selectedGrades.includes(level)
                              ? "bg-primary border-primary"
                              : "border-muted-foreground"
                          )}>
                            {selectedGrades.includes(level) && (
                              <svg className="w-3 h-3 text-primary-foreground" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </span>
                          <span className="text-sm font-medium">{level}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('filters.gradeDescription')}
                    </p>
                  </div>
                )}

                {/* Original Language Filter */}
                {showOriginalLanguageFilter && (
                  <div className="filter-group space-y-3">
                    <Label htmlFor="original-language" className="text-sm font-semibold text-foreground">
                      {t('filters.originalIn')}
                    </Label>
                    <SingleLanguageSelector
                      value={originalLanguageCode}
                      onChange={handleOriginalLanguageChange}
                      topLanguageCodes={topLanguageCodes}
                      languages={originalLanguages !== undefined ? originalLanguages : ALL_LANGUAGES}
                    />
                  </div>
                )}

                {/* Sort Filter */}
                {showSortFilter && (
                  <div className="filter-group space-y-3">
                    <Label htmlFor="sort-filter" className="text-sm font-semibold text-foreground">
                      {t('filters.sortBy')}
                    </Label>
                    <select
                      id="sort-filter"
                      value={selectedSort}
                      onChange={(e) => onSortChange(e.target.value as SortOption | '')}
                      className="sort-select w-full px-4 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">{t('filters.default')}</option>
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Panel Footer - Sticky */}
            <div className="filter-panel-footer sticky bottom-0 bg-background/90 backdrop-blur-sm border-t border-border px-6 py-4">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="flex-1"
                  disabled={!hasActiveFilters}
                >
                  {t('filters.clearAll')}
                </Button>
                <Button
                  onClick={handleApplyFilters}
                  className="flex-1"
                >
                  {t('filters.showResults')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

